import { memo, useCallback, useMemo, useSyncExternalStore, useState, useEffect } from 'react';
import { IconBackground } from '@tabler/icons-react';
import { Popover, Transition } from '@headlessui/react';
import tinycolor from 'tinycolor2';
import PlayerMenuTooltip from '../../../PlayerMenus/PlayerMenuTooltip';
import { getTextElementById } from '../utils';
import { updateTextElementProperties } from './textElementPopoverUtils';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { classnames } from '@/libs/utils';
import { ColorPicker } from '@/components/atoms/ColorPicker/ColorPicker';
import { brandKit } from '@/stores/brandKit';
import Toggle from '@/components/atoms/Toggle';

function TextElementBackgroundPopover({ textElementId }: { textElementId: string }) {
  const { clipId, clipData } = useClipsContext();
  const brandKitStore = useSyncExternalStore(brandKit.subscribe, brandKit.getSnapshot);
  const [sliderHeight, setSliderHeight] = useState<number | null>(null);
  const [lastBackgroundState, setLastBackgroundState] = useState<{
    color: string;
    opacity: number;
    height: number;
  } | null>(null);

  useEffect(() => {
    setLastBackgroundState(null);
  }, [textElementId]);

  const selectedElement = useMemo(() => {
    return getTextElementById(clipData, textElementId);
  }, [clipData, textElementId]);

  useEffect(() => {
    const background = selectedElement?.style?.background;
    if (sliderHeight !== null || !background) {
      return;
    }
    const gradientMatch = background.match(
      /linear-gradient\([^)]*,\s*transparent\s*0%,\s*transparent\s*(\d+)%,\s*(rgba?\([^)]+\))\s*\1%,\s*\2\s*100%\)/
    );
    if (gradientMatch) {
      const matchedHeight = Number.parseInt(gradientMatch[1] ?? '', 10);
      const apiHeight = Number.isNaN(matchedHeight) ? 49 : matchedHeight;
      setSliderHeight(apiHeight);
    } else {
      setSliderHeight(49);
    }
  }, [sliderHeight, selectedElement?.style?.background]);

  const { backgroundToggleEnabled, color, opacity } = useMemo(() => {
    const background = selectedElement?.style?.background;

    if (!background || background === 'transparent') {
      return {
        backgroundToggleEnabled: false,
        color: '#ffffff',
        opacity: 80
      };
    }

    // Extract color and height from linear gradient if it exists
    const gradientMatch = background.match(
      /linear-gradient\([^)]*,\s*transparent\s*0%,\s*transparent\s*(\d+)%,\s*(rgba?\([^)]+\))\s*\1%,\s*\2\s*100%\)/
    );
    let extractedColor = '#ffffff';
    let extractedOpacity = 80;

    if (gradientMatch) {
      const colorWithOpacity = gradientMatch[2];
      const parsedColor = tinycolor(colorWithOpacity);
      if (parsedColor.isValid()) {
        const rgbValues = parsedColor.toRgb();
        const hexColor = parsedColor.toHex();
        const alphaPercentage = Math.round(rgbValues.a * 100);
        extractedColor = `#${hexColor}`;
        extractedOpacity = alphaPercentage;
      }
    } else {
      const parsedColor = tinycolor(background);
      if (parsedColor.isValid()) {
        const rgbValues = parsedColor.toRgb();
        const hexColor = parsedColor.toHex();
        const alphaPercentage = Math.round(rgbValues.a * 100);
        extractedColor = `#${hexColor}`;
        extractedOpacity = alphaPercentage;
      }
    }

    return {
      backgroundToggleEnabled: true,
      color: extractedColor,
      opacity: extractedOpacity
    };
  }, [selectedElement?.style?.background]);

  const updateBackground = useCallback(
    (newValues: { backgroundToggleEnabled?: boolean; color?: string; opacity?: number; backgroundHeight?: number }) => {
      const updatedValues = {
        backgroundToggleEnabled: newValues.backgroundToggleEnabled ?? backgroundToggleEnabled,
        color: newValues.color ?? color,
        opacity: newValues.opacity ?? opacity,
        backgroundHeight: newValues.backgroundHeight ?? sliderHeight
      };

      if (!updatedValues.backgroundToggleEnabled) {
        updateTextElementProperties(clipId, clipData, textElementId, {
          style: {
            background: 'transparent'
          }
        });
        return;
      }

      // Use tinycolor2 to handle color conversion
      const colorWithOpacity = tinycolor(updatedValues.color)
        .setAlpha(updatedValues.opacity / 100)
        .toRgbString();

      // Create linear gradient background
      const backgroundGradient = `linear-gradient(180deg, transparent 0%, transparent ${updatedValues.backgroundHeight}%, ${colorWithOpacity} ${updatedValues.backgroundHeight}%, ${colorWithOpacity} 100%)`;

      updateTextElementProperties(clipId, clipData, textElementId, {
        style: {
          background: backgroundGradient
        }
      });
    },
    [backgroundToggleEnabled, color, opacity, sliderHeight, clipId, clipData, textElementId]
  );

  const onToggleBackground = useCallback(() => {
    if (backgroundToggleEnabled) {
      setLastBackgroundState({ color, opacity, height: sliderHeight ?? 49 });
      updateBackground({ backgroundToggleEnabled: false });
    } else {
      const restoredColor = lastBackgroundState?.color ?? color;
      const restoredOpacity = lastBackgroundState?.opacity ?? opacity;
      const restoredHeight = lastBackgroundState?.height ?? sliderHeight ?? 49;
      setSliderHeight(restoredHeight);
      updateBackground({
        backgroundToggleEnabled: true,
        color: restoredColor,
        opacity: restoredOpacity,
        backgroundHeight: restoredHeight
      });
    }
  }, [backgroundToggleEnabled, color, opacity, sliderHeight, lastBackgroundState, updateBackground]);

  const onBackgroundColorChange = useCallback(
    (value?: string) => {
      if (value) {
        updateBackground({ color: value });
      }
    },
    [updateBackground]
  );

  const onOpacityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateBackground({ opacity: Number(e.target.value) });
    },
    [updateBackground]
  );

  const onBackgroundHeightChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const displayValue = Number(e.target.value);
      const apiValue = 100 - displayValue;
      setSliderHeight(apiValue);
      updateBackground({ backgroundHeight: apiValue });
    },
    [updateBackground]
  );

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <Popover.Button
            data-testid="text-element-background-button"
            className={classnames(
              'group/button flex size-10 items-center justify-center rounded-md hover:bg-slate-100',
              open ? 'bg-slate-100' : 'hover:bg-slate-50'
            )}
          >
            <div className="flex w-10 cursor-pointer items-center justify-center rounded-md">
              <IconBackground size={16} />
              <PlayerMenuTooltip content="Background" />
            </div>
          </Popover.Button>
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Popover.Panel className="absolute z-50 mt-1 w-64 translate-y-1 space-y-5">
              <div className="space-y-4 rounded-lg border bg-white p-4 shadow-lg">
                {/* Enable/Disable Background */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Background</span>
                  <Toggle on={backgroundToggleEnabled} onToggle={onToggleBackground} />
                </div>

                {backgroundToggleEnabled && (
                  <>
                    {/* Background Color */}
                    <div className="grid grid-cols-12 items-center justify-between gap-2">
                      <span className="col-span-4 text-sm font-medium">Color</span>
                      <ColorPicker
                        defaultColors={brandKitStore.colors}
                        defaultColorsLabel="Background Color"
                        onChange={onBackgroundColorChange}
                        hex={color}
                        children={
                          <div className="flex size-full items-center justify-between bg-white p-2">
                            <div
                              className="size-6 rounded border"
                              style={{
                                background: color
                              }}
                            />
                            <span className="font-mono text-xs text-slate-400">{color}</span>
                          </div>
                        }
                        classNames="relative col-span-8 flex w-full items-center justify-between rounded-md border overflow-hidden"
                      />
                    </div>

                    {/* Opacity Slider */}
                    <div className="grid grid-cols-12 items-center justify-between gap-2">
                      <span className="col-span-4 text-sm font-medium">Opacity</span>
                      <span className="col-span-8 flex items-center gap-2">
                        <input
                          type="range"
                          min={0}
                          max={100}
                          value={opacity}
                          step={1}
                          onChange={onOpacityChange}
                          className="h-1 w-full cursor-pointer appearance-none rounded-xl bg-slate-300"
                        />
                        <span className="ml-1 w-8 shrink-0 text-right text-2xs tabular-nums text-slate-400">
                          {opacity}%
                        </span>
                      </span>
                    </div>

                    {/* Background Height */}
                    <div className="grid grid-cols-12 items-center justify-between gap-2">
                      <span className="col-span-4 text-sm font-medium">Height</span>
                      <span className="col-span-8 flex items-center gap-2">
                        <input
                          type="range"
                          min={0}
                          max={100}
                          value={100 - (sliderHeight ?? 49)}
                          step={1}
                          onChange={onBackgroundHeightChange}
                          className="h-1 w-full cursor-pointer appearance-none rounded-xl bg-slate-300"
                        />
                        <span className="ml-1 w-8 shrink-0 text-right text-2xs tabular-nums text-slate-400">
                          {100 - (sliderHeight ?? 49)}%
                        </span>
                      </span>
                    </div>
                  </>
                )}
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

export default memo(TextElementBackgroundPopover);
